import { useState } from "react";
import PaymentLayout from "../layouts/PaymentLinkLayouts";
import CustomerDetailForm from "./CustomerDetailForm";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import formatCurrency from "../../utils/numberFormatter";
import validateCustomerData from "../utils/validateCustomerData";
import darkenColor from "../utils/darkenColor"; // ✅ Import darkenColor terpisah
import { updateCustomerDetail } from "../../redux/slices/customerSlice";
import useTranslation from "../hooks/useTranslation";

const PaymentDetail = ({
  merchant_name,
  account_name,
  title,
  total_amount,
  reffNo,
  include_constumer_detail,
  required_customer_fields,
  status,
  payment_url,
  description,
  items,
  uneditable,
  isMultiplePayment,
  maxUsage,
  emailRequired,
  nameRequired,
  phoneRequired,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const customer = useSelector((state) => state.customer);
  const { secondary_color, secondary_text_color, tertiary_text_color, card_title } = useSelector((state) => state.style); // ⭐ Ambil secondary color & card_title
  const buttonTextColor = tertiary_text_color || secondary_text_color || "#FFFFFF";
  const [errors, setErrors] = useState({});
  const shouldCollectCustomerDetails = [true, 1, "1", "true"].includes(include_constumer_detail);
  const customerRequiredFields = {
    name: Boolean(nameRequired ?? required_customer_fields?.name),
    phone: Boolean(phoneRequired ?? required_customer_fields?.phone),
    email: Boolean(emailRequired ?? required_customer_fields?.email),
  };

  const handleCustomerChange = (event) => {
    if (uneditable) return;

    const { name } = event.target;
    let { value } = event.target;

    if (name === "phone") {
      value = value.replace(/^\+62/, "0");
      if (value.startsWith("62") && !value.startsWith("0")) {
        value = `0${value.substring(2)}`;
      }
    }

    dispatch(updateCustomerDetail({ [name]: value }));
  };

  const handleNext = () => {
    const validationFields = shouldCollectCustomerDetails ? customerRequiredFields : required_customer_fields;
    const validationErrors = validateCustomerData(customer, validationFields);

    if ((shouldCollectCustomerDetails || include_constumer_detail) && Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      navigate(`/method/${reffNo}`, { replace: true });
    }
  };

  const [integerPart] = formatCurrency(total_amount).split(",");

  // Check if items exist and not empty
  const hasItems = items && Array.isArray(items) && items.length > 0;
  const inputClass = `mt-2 h-10 w-full rounded-xl border border-gray-200 px-4 text-base text-gray-900 outline-none transition focus:border-singaHijauPrimary ${
    uneditable ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"
  }`;
  const labelClass = "block text-sm font-medium text-gray-900";
  const errorClass = "mt-1 text-sm text-red-500";

  if (shouldCollectCustomerDetails) {
    return (
      <PaymentLayout title={card_title || merchant_name}>
        <div className="flex flex-col gap-6">
          <h3 className="text-lg font-semibold text-gray-900">{t("paymentDetail.yourDetails")}</h3>

          <div className="flex flex-col gap-4">
            <div>
              <label className={labelClass} htmlFor="customerFullName">
                {t("common.name")} {customerRequiredFields.name && <span className="text-red-500">*</span>}
              </label>
              <input
                id="customerFullName"
                type="text"
                name="fullName"
                value={customer.fullName}
                onChange={handleCustomerChange}
                readOnly={uneditable}
                required={customerRequiredFields.name}
                className={inputClass}
                placeholder={t("paymentDetail.inputName")}
              />
              {errors.fullName && <p className={errorClass}>{errors.fullName}</p>}
            </div>

            <div>
              <label className={labelClass} htmlFor="customerPhone">
                {t("common.phoneNumber")} {customerRequiredFields.phone && <span className="text-red-500">*</span>}
              </label>
              <input
                id="customerPhone"
                type="tel"
                name="phone"
                value={customer.phone}
                onChange={handleCustomerChange}
                readOnly={uneditable}
                required={customerRequiredFields.phone}
                className={inputClass}
                placeholder={t("paymentDetail.inputPhoneNumber")}
              />
              {errors.phone && <p className={errorClass}>{errors.phone}</p>}
            </div>

            <div>
              <label className={labelClass} htmlFor="customerEmail">
                {t("common.email")} {customerRequiredFields.email && <span className="text-red-500">*</span>}
              </label>
              <input
                id="customerEmail"
                type="email"
                name="email"
                value={customer.email}
                onChange={handleCustomerChange}
                readOnly={uneditable}
                required={customerRequiredFields.email}
                className={inputClass}
                placeholder={t("paymentDetail.inputEmail")}
              />
              {errors.email && <p className={errorClass}>{errors.email}</p>}
            </div>
          </div>

          <button
            onClick={handleNext}
            className="w-full h-10 rounded-xl text-sm font-medium transition-colors text-white"
            style={{
              backgroundColor: secondary_color,
              color: buttonTextColor,
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = darkenColor(secondary_color, 15);
              e.target.style.color = darkenColor(buttonTextColor, 15);
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = secondary_color;
              e.target.style.color = buttonTextColor;
            }}
          >
            {t("paymentDetail.continue")}
          </button>
        </div>
      </PaymentLayout>
    );
  }

  return (
    <PaymentLayout title={card_title || merchant_name}>
      <div>
        <p className="text-gray-600 text-lg">{title}</p>
        <h2 className="text-5xl font-bold mt-2">
          <span>{integerPart}</span>
        </h2>
        <p className="text-gray-500 mt-2">ID {reffNo}</p>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-2xl font-bold mb-4">{t("paymentDetail.paymentDetailTitle")}</h3>
        <div className="grid gap-1">
          <div>
            <span className="text-sm text-gray-500">{t("paymentDetail.to")}</span>
            <div className="text-base font-medium">
              {merchant_name} [{account_name}]
            </div>
          </div>

          {/* Items Section */}
          {hasItems && (
            <div className="mt-3">
              <span className="text-sm text-gray-500 font-semibold">{t("paymentDetail.items")}</span>
              <div className="mt-2 space-y-2">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex-1">
                      <p className="text-base font-medium text-gray-800">{item.name}</p>
                      <p className="text-sm text-gray-500">
                        {item.quantity} × {formatCurrency(item.unit_price).split(",")[0]}
                      </p>
                    </div>
                    <div className="text-base font-semibold text-gray-900">
                      {formatCurrency(item.subtotal).split(",")[0]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 pt-2 border-t border-gray-200">
            <span className="text-sm text-gray-500">{t("paymentDetail.totalAmount")}</span>
            <div className="text-lg font-bold text-gray-900">
              <span>{integerPart}</span>
            </div>
          </div>
        </div>
      </div>

      {include_constumer_detail && (
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-2xl font-bold mb-4">{t("paymentDetail.customerDetail")}</h3>
          <CustomerDetailForm requiredFields={required_customer_fields} setErrors={setErrors} uneditable={uneditable} />
          {Object.keys(errors).length > 0 && (
            <div className="text-red-500 text-sm mt-2">
              {Object.values(errors).map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ⭐ Button dengan dynamic secondary color */}
      <button
        onClick={handleNext}
        className="w-full h-10 rounded-xl text-sm font-medium transition-colors text-white"
        style={{
          backgroundColor: secondary_color,
          color: buttonTextColor,
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = darkenColor(secondary_color, 15);
          e.target.style.color = darkenColor(buttonTextColor, 15);
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = secondary_color;
          e.target.style.color = buttonTextColor;
        }}
      >
        {t("paymentDetail.next")}
      </button>
    </PaymentLayout>
  );
};

export default PaymentDetail;
